import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import axios from "./index";
import { p2e, a2e } from "@utils";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { getCSRFToken } from "../../utils/csrf";
import toastManager from "../../utils/toastManager";

const AxiosInterceptors = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    // Request Interceptor
    const requestInterceptor = axios.interceptors.request.use(
      async (config) => {
        if (config.headers["Content-Type"] !== "multipart/form-data") {
          if (typeof config.data === "object" && config.data !== null) {
            try {
              config.data = JSON.parse(p2e(a2e(JSON.stringify(config.data))));
            } catch (error) {
              console.error("Number conversion error:", error);
            }
          }
        }

        // if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
        //   const token = await getCSRFToken();
        //   if (token) {
        //   config.headers['X-CSRF-Token'] = token;
        //   }
        // }

        const language = i18next.language || localStorage.getItem("language") || "en";
        config.headers["Lang"] = language;

        return config;
      },
      (error) => {
        console.error("API Request Error:", error);
        return Promise.reject(error);
      }
    );

    // Response Interceptor
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        if (response?.data?.code === 401) {
          console.log("Received 401 error code in response data");
          return response;
        }

        // Skip global error handling if the request has skipGlobalErrorHandler flag
        if (response?.config?.skipGlobalErrorHandler) {
          console.log("✅ AxiosInterceptor: Skipping global error handler for this request");
          return response;
        }

        if (response?.data?.code && response.data.code !== 200) {
          toastManager.showError(response.data.error || t("error_generic"));
          console.error("API Error:", response.data.error);
        }

        return response;
      },
      (error) => {
        // Skip error handling if this is a token expiration error being handled by jwtService
        if (error._handledByTokenExpiration) {
          console.log("✅ AxiosInterceptor: Skipping error handling - token expiration in progress");
          return Promise.reject(error);
        }

        // Skip global error handling if the request has skipGlobalErrorHandler flag
        if (error.config?.skipGlobalErrorHandler) {
          console.log("✅ AxiosInterceptor: Skipping global error handler for this request (error case)");
          return Promise.reject(error);
        }

        switch (true) {
          case !!error.response: {
            const { status, data } = error.response;

            switch (status) {
              case 401:
                console.log("401 error handled by main axios instance");
                // Don't show error toast for 401 - jwtService handles it
                break;

              case 403:
                // Check if this is a KYC verification error that should be handled by custom logic
                // Check for both English and Persian KYC error messages
                const isKycError = data?.code === 403 && (
                  data?.message?.includes("authentication level") || // English: deposit ID
                  data?.message?.includes("احراز هویت") || // Persian: verification
                  data?.message?.includes("سطح کاربری") || // Persian: user level
                  data?.message?.includes("turnover") || // English: trading volume
                  data?.message?.includes("financial") || // English: financial requirements
                  data?.message?.includes("گردش مالی") || // Persian: trading volume
                  data?.message?.includes("ناموفق") || // Persian: failed/unsuccessful
                  data?.message?.includes("قبلاً ثبت شده") || // Persian: already registered
                  data?.message?.toLowerCase().includes("already") // English: already exists
                );

                if (isKycError) {
                  // Mark this error as handled to prevent global toast
                  error._handledByCustomLogic = true;
                  console.log("✅ 403 KYC/Level2 error - marked as handled, will be processed by custom logic");
                } else {
                  toastManager.showError(error);
                }
                break;

              default:
                console.log("🔴 AxiosInterceptor: Default error handler", error.response?.status, error.response?.data);
                console.log("🔍 AxiosInterceptor: Request URL:", error.config?.url);
                console.log("🔍 AxiosInterceptor: Checking _handledByCustomLogic flag:", error._handledByCustomLogic);

                // Check if this is an auth-related endpoint that has custom error handling in page-level catch blocks
                const requestUrl = error.config?.url || '';
                const isAuthEndpoint = requestUrl.includes('/login') ||
                                      requestUrl.includes('/register') ||
                                      requestUrl.includes('/forgot-password') ||
                                      requestUrl.includes('/reset-password') ||
                                      requestUrl.includes('/update-password') ||
                                      requestUrl.includes('/two-step') ||
                                      requestUrl.includes('/verify') ||
                                      requestUrl.includes('/auth/google');

                // Check if this is a KYC verification error that should be handled by custom logic
                const responseData = error.response?.data;
                const isKycVerificationError = status === 404 && responseData?.code === 404 && (
                  responseData?.message?.includes("national") || // English
                  responseData?.message?.includes("ملی") || // Persian: national
                  responseData?.message?.includes("mobile") || // English
                  responseData?.message?.includes("موبایل") // Persian: mobile
                );

                if (isAuthEndpoint) {
                  // Auth endpoints handle their own errors in catch blocks - don't show global toast
                  console.log("✅ AxiosInterceptor: Auth endpoint detected - skipping global error toast");
                } else if (isKycVerificationError) {
                  // Mark this error as handled to prevent global toast
                  error._handledByCustomLogic = true;
                  console.log("✅ AxiosInterceptor: KYC verification error detected - marked as handled");
                } else if (!error._handledByCustomLogic) {
                  console.log("🚨 AxiosInterceptor: Showing error toast (flag not set)");
                  toastManager.showError(error);
                } else {
                  console.log("✅ AxiosInterceptor: Skipping error toast (flag is set)");
                }
                break;
            }
            break;
          }

          case !!error.request: {
            console.error("🔴 AxiosInterceptor: No response received:", error.request);
            console.log("🔍 AxiosInterceptor: Request error - checking _handledByCustomLogic flag:", error._handledByCustomLogic);

            if (!error._handledByCustomLogic) {
              console.log("🚨 AxiosInterceptor: Showing request error toast (flag not set)");
              toastManager.showError(error);
            } else {
              console.log("✅ AxiosInterceptor: Skipping request error toast (flag is set)");
            }
            break;
          }

          default: {
            const msg = error.message?.toLowerCase() || "";
            console.log("🔴 AxiosInterceptor: Default case error:", error.message);
            console.log("🔍 AxiosInterceptor: Default case - checking _handledByCustomLogic flag:", error._handledByCustomLogic);

            if (!msg.includes("cancel")) {
              if (!error._handledByCustomLogic) {
                console.log("🚨 AxiosInterceptor: Showing default error toast (flag not set)");
                toastManager.showError(error);
              } else {
                console.log("✅ AxiosInterceptor: Skipping default error toast (flag is set)");
              }
            } else {
              console.log("Request canceled:", error.message);
            }
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [dispatch, history]);

  return null;
};

export default AxiosInterceptors;
